import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ORG_KIND_LABELS, type IOrgSummary } from '@stem/contracts';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';
import { FullPageSpinner } from '../shell/Spinner';

/**
 * Which organisation to work in.
 *
 * Step 3 of the two-token flow rendered as a screen. A teacher who works at two
 * schools sees both; most people see one school and their personal workspace,
 * and are sent straight through.
 */
export function OrgSwitcherPage() {
  const navigate = useNavigate();
  const setSession = useSession(state => state.setSession);
  const profile = useSession(state => state.profile);

  const { data, isLoading, error } = useQuery({
    queryKey: ['orgs'],
    queryFn: () => apiClient.user<IOrgSummary[]>('get', '/auth/orgs'),
  });

  useEffect(() => {
    if (!data || !profile) return;
    setSession(profile, data);

    // One real organisation and nothing to choose between: choosing for them is
    // not a shortcut, it is the absence of a pointless screen.
    const real = data.filter(org => org.kind !== 'personal');
    if (real.length === 1) navigate(`/o/${real[0].slug}`, { replace: true });
    else if (data.length === 1) navigate(`/o/${data[0].slug}`, { replace: true });
  }, [data, profile, navigate, setSession]);

  if (isLoading) return <FullPageSpinner label="Finding your organisations…" />;

  if (error) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <p className="text-sm text-red-700">{(error as Error).message}</p>
        <Link to="/login" className="btn-primary mt-4">
          Sign in again
        </Link>
      </div>
    );
  }

  const orgs = data ?? [];

  return (
    <div className="mx-auto max-w-xl px-4 py-12">
      <h1 className="text-2xl font-bold text-brand-900">Choose where to work</h1>
      <p className="mt-1 text-sm text-ink-600">
        You can switch at any time without signing in again.
      </p>

      <ul className="mt-6 space-y-2">
        {orgs.map(org => (
          <li key={org.id}>
            <Link
              to={`/o/${org.slug}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-ink-200 bg-white p-4 hover:border-brand-400"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink-900">{org.name}</p>
                <p className="text-xs text-ink-500">
                  {ORG_KIND_LABELS[org.kind] ?? org.kind} · you are {org.role}
                </p>
              </div>
              <span aria-hidden="true" className="text-ink-400">
                →
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {orgs.length === 0 && (
        <p className="card mt-6 text-sm text-ink-600">
          You do not belong to any organisation yet.{' '}
          <Link to="/onboarding" className="text-brand-700 underline">
            Join one with an invitation code
          </Link>
          .
        </p>
      )}

      <p className="mt-6 text-center text-xs text-ink-500">
        <Link to="/onboarding" className="underline">
          Join another organisation
        </Link>
      </p>
    </div>
  );
}
